function removeAllPromoDiscountForProduct(
   productCode,
   discount_list,
   promotionalDiscounts,
   setPromotionalDiscounts,
) {
   let to_return_promos = promotionalDiscounts
   for (const [discount_key, value] of Object.entries(promotionalDiscounts)) {
      if (
         discount_key != 'numberDiscountValues' &&
         discount_list[discount_key]['ApplyTo'].split(',').includes(productCode)
      )
         delete to_return_promos[discount_key]
   }
   setPromotionalDiscounts(to_return_promos)
   return to_return_promos
}

function removePromoDiscountNotInCurrentBook(
   current_discount_list,
   promotionalDiscounts,
   setPromotionalDiscounts,
) {
   let to_return_promos = promotionalDiscounts
   for (const [discount_key, value] of Object.entries(promotionalDiscounts)) {
      if (
         discount_key in current_discount_list ||
         discount_key == 'numberDiscountValues'
      ) {
         continue
      } else {
         delete to_return_promos[discount_key]
         setPromotionalDiscounts((prev) => {
            return {
               ...promotionalDiscounts,
               [discount_key]: false,
            }
         })
      }
   }
   return to_return_promos
}

/**
 * Returns true when the user's role has the given feature permission.
 * Mirrors useFeaturePermissions().hasPermission: Super Admin is always granted
 * (unless superAdminValue is false); otherwise the role must be listed for the permission.
 *
 * @param {Object} state - The current state object (expects user_role and featurePermissions).
 * @param {string} permission - The feature permission code to check.
 * @param {boolean} [superAdminValue=true] - Whether Super Admin implicitly has the permission.
 * @returns {boolean} - True if the role has the permission.
 */
function hasFeaturePermission(state, permission, superAdminValue = true) {
   if (superAdminValue && state.user_role === 'Super Admin') return true
   return (
      state.featurePermissions?.[permission]?.includes(state.user_role) ?? false
   )
}

/**
 * Determines whether a discount should be disabled based on the current state of the quote and products.
 * - If the discount's applyTo list includes any of the selected products, the discount is enabled.
 * - If the discount's requiredProducts list includes all required products, the discount is enabled.
 * - If requiredProducts is null, skip this check as none are required.
 *
 * @param {Object} state - The current state object containing product counts and discount information.
 * @param {string} discountCode - The code representing the discount to be checked.
 * @param {Object} [options] - Optional flags.
 * @param {boolean} [options.ignoreDealDeskLock=false] - When true, deal desk locks do not disable
 *   the discount. Used to derive the selection/checked state so a deal desk lock never deselects a
 *   previously selected discount.
 * @returns {boolean} - True if the discount should be disabled, false otherwise.
 */
function shouldDisableDiscount(
   state,
   discountCode,
   { ignoreDealDeskLock = false } = {},
) {
   let selectedProducts = {
      addJobPosting: state.productAdditionalJobsCount > 0,
      addStorage: state.productAdditionalStorageCount > 0,
      benAdmin: state.productBenefitsAdministrationEECount > 0,
      benAdminImp: state.benAdminImpType !== 'none',
      [state.coreProductSelected]:
         state.coreProductSelected !== 'core0' && state.productCoreEECount > 0,
      [state.coreProductSelected + 'Imp']: state.impType === 'full',
      [state.coreProductSelected + 'Quickstart']:
         state.impType === 'quickstart',
      historicalDataImport: state.productHistoricalDataImport,
      marketplaceListing: state.productMarketplaceListing,
      paidService: state.productPaidServiceAmount > 0,
      payroll: state.productPayrollTotalEECount > 0,
      payrollImp:
         state.payrollImpType === 'full' ||
         state.productPayrollEINImpTypes.includes('full'),
      payrollImpFoundations:
         state.payrollImpType === 'foundations' ||
         state.productPayrollEINImpTypes.includes('foundations'),
      performanceMan: state.productPerformanceEECount > 0,
      sponsorship: state.productSponsorship,
      timeTracking: state.productTimeTrackingEECount > 0,
      testAccount: state.productTestAccount,
      monthlyTotal: state.monthlyTotal.listPrice > 0,
      oneTimeTotal: state.oneTimeTotal.listPrice > 0,
      // "whatsThis": whatsThis
   }
   // Add all entries from dynamicProducts, with true/false based on value > 0
   state.dynamicProducts?.forEach((value, key) => {
      selectedProducts[key] = value > 0
   })

   // Append Consulting and Professional Services to selectedProducts
   state.consultingServiceDetails.forEach(
      (service) => (selectedProducts[service.serviceCode] = service.count > 0),
   )
   state.professionalServiceDetails.forEach(
      (service) => (selectedProducts[service.serviceCode] = service.count > 0),
   )

   let disable_discount = true
   let apply_to_check = false
   let required_products_check = true
   let depends_on_check = false
   let deal_desk_lock_check = true
   let has_permissions_for_number_discount = true
   let term_prepay_length_check = true

   // NEW: Check term and prepay length constraints
   const discount = state.discount_list[discountCode]
   const termLength = state.termLength || 1
   const prepayLength = state.prepayLength || 1

   // Check TermLengthMin constraint
   if (
      discount['TermLengthMin'] != null &&
      termLength < discount['TermLengthMin']
   ) {
      term_prepay_length_check = false
   }
   // Check TermLengthMax constraint
   if (
      discount['TermLengthMax'] != null &&
      termLength > discount['TermLengthMax']
   ) {
      term_prepay_length_check = false
   }
   // Check PrepayLengthMin constraint
   if (
      discount['PrepayLengthMin'] != null &&
      prepayLength < discount['PrepayLengthMin']
   ) {
      term_prepay_length_check = false
   }
   // Check PrepayLengthMax constraint
   if (
      discount['PrepayLengthMax'] != null &&
      prepayLength > discount['PrepayLengthMax']
   ) {
      term_prepay_length_check = false
   }

   // Check if any of the selected products are in the discount's applyTo list
   for (const productCode of state.discount_list[discountCode][
      'ApplyTo'
   ]?.split(',') || []) {
      if (selectedProducts[productCode]) {
         apply_to_check = true
         break
      }
   }
   // Check if all required products are selected. If RequiredProducts is null, skip this check as none are required
   if (state.discount_list[discountCode]['RequiredProducts'] != null) {
      const requiredProducts =
         state.discount_list[discountCode]['RequiredProducts'].split(',')
      if (!checkRequiredProducts(state, selectedProducts, requiredProducts)) {
         required_products_check = false
      }
   }

   // Check if the discount depends on another prouduct and checks the upper and lower bounds
   if (state.discount_list[discountCode]['DependsOn'] != null) {
      const productCount = getProductCount(
         state,
         state.discount_list[discountCode]['DependsOn'],
      )
      if (
         productCount >= state.discount_list[discountCode]['EmpLow'] &&
         productCount <= state.discount_list[discountCode]['EmpHigh']
      ) {
         depends_on_check = true
      }
   } else {
      depends_on_check = true
   }

   // Check if this is a deal desk discount (UUID format or temp-* ID)
   const isDealDeskDiscount =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
         discountCode,
      ) || discountCode.startsWith('temp-')

   // Deal desk locks are skipped entirely when the caller asks to ignore them (used to derive
   // selection state) or when the user can stack discounts on deal desk locked products.
   const skipDealDeskLock =
      ignoreDealDeskLock ||
      hasFeaturePermission(state, 'AllowDealDeskDiscountStacking')

   // Check if discount is an implementation discount and if the deal desk is locked. If so, disable the discount input element in its current state, regardless if it is true or false.
   if (
      !skipDealDeskLock &&
      !(
         state?.ignoreHRISImplementationDealDeskDiscountList?.has(
            discountCode,
         ) ||
         state?.ignorePayrollImplementationDealDeskDiscountList?.has(
            discountCode,
         ) ||
         state?.ignoreQuickstartDealDeskDiscountList?.has(discountCode) ||
         isDealDeskDiscount // Deal desk discounts should not be disabled by their own locks
      )
   ) {
      // Special exception: Allow discounts that apply to benAdminImp to stack with deal desk discounts
      // TODO: Remove after DD revamp. This exception will likely be removed in the future
      if (
         state.discount_list[discountCode]['ApplyTo']?.includes('benAdminImp')
      ) {
         deal_desk_lock_check = true
      } else {
         for (const productCode of state.discount_list[discountCode][
            'ApplyTo'
         ]?.split(',') || []) {
            if (selectedProducts[productCode]) {
               state.dealDeskLocks.has(productCode)
                  ? (deal_desk_lock_check = false)
                  : (deal_desk_lock_check = true)
            }
         }
      }
   }

   if (
      state.discount_list[discountCode]['DiscountType'] === 'percent' &&
      state.discount_list[discountCode]['MaxDiscount'] > 0 &&
      !(
         state.discount_list[discountCode]['RoleRequired']
            ?.split(',')
            ?.includes(state.user_role) ?? false
      ) &&
      state.user_role !== 'Super Admin'
   ) {
      has_permissions_for_number_discount = false
   }

   // Deal desk discounts should always be enabled (they manage their own product selection)
   if (isDealDeskDiscount) {
      return false
   }

   // If at least one of the selected products is in the discount's applyTo list, and required products are met, enable the discount
   if (
      apply_to_check &&
      required_products_check &&
      depends_on_check &&
      deal_desk_lock_check &&
      has_permissions_for_number_discount &&
      term_prepay_length_check
   ) {
      disable_discount = false
   }

   return disable_discount
}

/**
 * Determines whether a discount option should be disabled in the family dropdown.
 * This is similar to shouldDisableDiscount but excludes role permission checks,
 * allowing users to select discounts they don't have permissions for (the input will be disabled instead).
 *
 * @param {Object} state - The current state object containing product counts and discount information.
 * @param {string} discountCode - The code representing the discount to be checked.
 * @returns {boolean} - True if the discount option should be disabled in the dropdown, false otherwise.
 */
function shouldDisableDiscountFamilyOption(state, discountCode) {
   let selectedProducts = {
      addJobPosting: state.productAdditionalJobsCount > 0,
      addStorage: state.productAdditionalStorageCount > 0,
      benAdmin: state.productBenefitsAdministrationEECount > 0,
      benAdminImp: state.benAdminImpType !== 'none',
      [state.coreProductSelected]:
         state.coreProductSelected !== 'core0' && state.productCoreEECount > 0,
      [state.coreProductSelected + 'Imp']: state.impType === 'full',
      [state.coreProductSelected + 'Quickstart']:
         state.impType === 'quickstart',
      historicalDataImport: state.productHistoricalDataImport,
      marketplaceListing: state.productMarketplaceListing,
      paidService: state.productPaidServiceAmount > 0,
      payroll: state.productPayrollTotalEECount > 0,
      payrollImp:
         state.payrollImpType === 'full' ||
         state.productPayrollEINImpTypes.includes('full'),
      payrollImpFoundations:
         state.payrollImpType === 'foundations' ||
         state.productPayrollEINImpTypes.includes('foundations'),
      performanceMan: state.productPerformanceEECount > 0,
      sponsorship: state.productSponsorship,
      timeTracking: state.productTimeTrackingEECount > 0,
      testAccount: state.productTestAccount,
      monthlyTotal: state.monthlyTotal.listPrice > 0,
      oneTimeTotal: state.oneTimeTotal.listPrice > 0,
   }

   // Add all entries from dynamicProducts, with true/false based on value > 0
   state.dynamicProducts?.forEach((value, key) => {
      selectedProducts[key] = value > 0
   })

   // Append Consulting and Professional Services to selectedProducts
   state.consultingServiceDetails.forEach(
      (service) => (selectedProducts[service.serviceCode] = service.count > 0),
   )
   state.professionalServiceDetails.forEach(
      (service) => (selectedProducts[service.serviceCode] = service.count > 0),
   )

   let disable_discount = true
   let apply_to_check = false
   let required_products_check = true
   let depends_on_check = false
   let deal_desk_lock_check = true
   let term_prepay_length_check = true

   // NEW: Check term and prepay length constraints
   const discount = state.discount_list[discountCode]
   const termLength = state.termLength || 1
   const prepayLength = state.prepayLength || 1

   // Check TermLengthMin constraint
   if (
      discount['TermLengthMin'] != null &&
      termLength < discount['TermLengthMin']
   ) {
      term_prepay_length_check = false
   }
   // Check TermLengthMax constraint
   if (
      discount['TermLengthMax'] != null &&
      termLength > discount['TermLengthMax']
   ) {
      term_prepay_length_check = false
   }
   // Check PrepayLengthMin constraint
   if (
      discount['PrepayLengthMin'] != null &&
      prepayLength < discount['PrepayLengthMin']
   ) {
      term_prepay_length_check = false
   }
   // Check PrepayLengthMax constraint
   if (
      discount['PrepayLengthMax'] != null &&
      prepayLength > discount['PrepayLengthMax']
   ) {
      term_prepay_length_check = false
   }

   // Check if any of the selected products are in the discount's applyTo list
   for (const productCode of state.discount_list[discountCode][
      'ApplyTo'
   ]?.split(',') || []) {
      if (selectedProducts[productCode]) {
         apply_to_check = true
         break
      }
   }

   // Check if all required products are selected. If RequiredProducts is null, skip this check as none are required
   if (state.discount_list[discountCode]['RequiredProducts'] != null) {
      const requiredProducts =
         state.discount_list[discountCode]['RequiredProducts'].split(',')
      if (!checkRequiredProducts(state, selectedProducts, requiredProducts)) {
         required_products_check = false
      }
   }

   // Check if the discount depends on another product and checks the upper and lower bounds
   if (state.discount_list[discountCode]['DependsOn'] != null) {
      const productCount = getProductCount(
         state,
         state.discount_list[discountCode]['DependsOn'],
      )
      if (
         productCount >= state.discount_list[discountCode]['EmpLow'] &&
         productCount <= state.discount_list[discountCode]['EmpHigh']
      ) {
         depends_on_check = true
      }
   } else {
      depends_on_check = true
   }

   // Users who can stack discounts on deal desk locked products are never blocked by locks here.
   const skipDealDeskLock = hasFeaturePermission(
      state,
      'AllowDealDeskDiscountStacking',
   )

   // Check if discount is an implementation discount and if the deal desk is locked
   if (
      !skipDealDeskLock &&
      !(
         state?.ignoreHRISImplementationDealDeskDiscountList?.has(
            discountCode,
         ) ||
         state?.ignorePayrollImplementationDealDeskDiscountList?.has(
            discountCode,
         ) ||
         state?.ignoreQuickstartDealDeskDiscountList?.has(discountCode)
      )
   ) {
      // Special exception: Allow discounts that apply to benAdminImp to stack with deal desk discounts
      if (
         state.discount_list[discountCode]['ApplyTo']?.includes('benAdminImp')
      ) {
         deal_desk_lock_check = true
      } else {
         for (const productCode of state.discount_list[discountCode][
            'ApplyTo'
         ]?.split(',') || []) {
            if (selectedProducts[productCode]) {
               state.dealDeskLocks.has(productCode)
                  ? (deal_desk_lock_check = false)
                  : (deal_desk_lock_check = true)
            }
         }
      }
   }

   // NOTE: We intentionally exclude the role permission check here
   // This allows users to select discounts they don't have permissions for
   // The input will be disabled instead when rendered

   // If at least one of the selected products is in the discount's applyTo list, and required products are met, enable the discount
   if (
      apply_to_check &&
      required_products_check &&
      depends_on_check &&
      deal_desk_lock_check &&
      term_prepay_length_check
   ) {
      disable_discount = false
   }

   return disable_discount
}

/**
 * Retrieves the count of a specific product based on the provided product code.
 *
 * @param {Object} state - The current state object containing product counts.
 * @param {string} productCode - The code representing the product whose count is to be retrieved.
 * @returns {number} - The count of the specified product.
 */
function getProductCount(state, productCode) {
   if (productCode === 'addJobPosting') return state.productAdditionalJobsCount
   if (productCode === 'addStorage') return state.productAdditionalStorageCount
   if (productCode === 'benAdmin')
      return state.productBenefitsAdministrationEECount
   if (productCode === 'paidService') return state.productPaidServiceAmount
   if (productCode === 'payroll') return state.productPayrollTotalEECount
   if (productCode === 'performanceMan') return state.productPerformanceEECount
   if (productCode === 'timeTracking') return state.productTimeTrackingEECount
   if (
      productCode === 'core1' ||
      productCode === 'core2' ||
      productCode === 'core3'
   )
      return state.productCoreEECount
   // Fall back to dynamicProducts for any product tracked there (e.g. EORGeo1, EORGeo2, ContractorManagement).
   // Booleans coerce to 1/0 so headcount-style comparisons (count >= EmpLow) behave correctly.
   const raw = state.dynamicProducts?.get(productCode)
   if (typeof raw === 'boolean') return raw ? 1 : 0
   return raw ?? 0
}

/**
 * Checks if the required product is selected. If the product is not selected, the discount should be disabled.
 *
 * @param {Object} state - The current state object containing product counts and discount information.
 * @param {Object} selectedProducts - The current state object containing product counts.
 * @param {string[]} requiredProducts - The list of products that are required for the discount to be enabled.
 * @returns {boolean} - True if the required product is selected, false otherwise.
 */
function checkRequiredProducts(state, selectedProducts, requiredProducts) {
   let hasProducts = false

   // Check the product_list and get all the products with ProductFamily HR
   const hrCoreProductFamilyProducts = Object.keys(state.product_list).filter(
      (key) =>
         state.product_list[key]['ProductCode'] !== 'core0' &&
         state.product_list[key]['ProductFamily'] === 'HR Core',
   )
   // if requiredProducts includes any of the products in the hrCoreProductFamilyProducts, check if any of them are selected
   if (requiredProducts.some((r) => hrCoreProductFamilyProducts.includes(r))) {
      ;({ hasProducts, requiredProducts } = checkRequiredProductGroup(
         hrCoreProductFamilyProducts,
         requiredProducts,
         selectedProducts,
      ))
      if (!hasProducts) return hasProducts
   }

   // Check the product_list and get all the products with ProductFamily HR implementation
   const hrImpProductFamilyProducts = Object.keys(state.product_list).filter(
      (key) => state.product_list[key]['ProductFamily'] === 'HR implementation',
   )
   // if requiredProducts includes any of the products in the hrImpProductFamilyProducts, check if any of them are selected
   if (requiredProducts.some((r) => hrImpProductFamilyProducts.includes(r))) {
      ;({ hasProducts, requiredProducts } = checkRequiredProductGroup(
         hrImpProductFamilyProducts,
         requiredProducts,
         selectedProducts,
      ))
      if (!hasProducts) return hasProducts
   }

   // Check the product_list and get all the products with ProductFamily Payroll Implementation
   const payrollImpProductFamilyProducts = Object.keys(
      state.product_list,
   ).filter(
      (key) =>
         state.product_list[key]['ProductFamily'] === 'Payroll Implementation',
   )
   // if requiredProducts includes any of the products in the payrollImpProductFamilyProducts, check if any of them are selected
   if (
      requiredProducts.some((r) => payrollImpProductFamilyProducts.includes(r))
   ) {
      ;({ hasProducts, requiredProducts } = checkRequiredProductGroup(
         payrollImpProductFamilyProducts,
         requiredProducts,
         selectedProducts,
      ))
      if (!hasProducts) return hasProducts
   }

   let productCode = requiredProducts.pop()
   while (productCode) {
      // Check if the product is on the selectedProducts list which means
      hasProducts = selectedProducts[productCode]

      // If the product is not selected, break the loop with hasProducts as false
      if (!hasProducts) break

      // Sets the productCode to the next product in the requiredProducts
      productCode = requiredProducts.pop()
   }

   return hasProducts
}
/**
 * Check if at least one product of the product group is selected in the selectedProducts that also exists in the requiredProducts
 *
 * @param {String[]} productGroup A string array of product codes for the family
 * @param {String[]} requiredProducts A string array of product codes that are required
 * @param {Object} selectedProducts An object with the product codes as keys and boolean values
 * @returns {Object} An object with hasProducts as a boolean and requiredProducts as a string array
 */

function checkRequiredProductGroup(
   productGroup,
   requiredProducts,
   selectedProducts,
) {
   // check if at least one product of the product group is selected in the selectedProducts that also exists in the requiredProducts
   let hasProducts = false
   for (const productCode of productGroup) {
      if (
         selectedProducts[productCode] &&
         requiredProducts.includes(productCode)
      ) {
         hasProducts = true
         break
      }
   }

   // Remove all the products in the product group from the requiredProducts since at least one of them is selected
   requiredProducts = requiredProducts.filter(
      (item) => !productGroup.includes(item),
   )
   return { hasProducts, requiredProducts }
}

/**
 * Determines whether a product should be disabled because the currently selected
 * term length falls outside the product's TermLengthMin / TermLengthMax range.
 * Either bound may be null/undefined, in which case that side is open-ended.
 *
 * @param {Object} product - Product object from product_list (must include TermLengthMin/Max).
 * @param {number} termLength - The currently selected term length (months).
 * @returns {boolean} - True if the product is outside the allowed term range.
 */
function isProductOutsideTermRange(product, termLength) {
   if (!product) return false
   const min = product.TermLengthMin
   const max = product.TermLengthMax
   if (min != null && termLength < min) return true
   if (max != null && termLength > max) return true
   return false
}

/**
 * Builds a friendly sentence describing a product's term-length availability,
 * used in tooltips and the term-change warning modal.
 *
 *   min + max  → "<name> is only available for Extended Term quotes from N to M months."
 *   min only   → "<name> is only available for Extended Term quotes of N months or more."
 *   max only   → "<name> is only available for Extended Term quotes of M months or less."
 *   neither    → "" (caller should not display anything)
 *
 * @param {Object} product
 * @returns {string}
 */
function formatTermRange(product) {
   const name = product?.ProductName ?? 'This product'
   const min = product?.TermLengthMin
   const max = product?.TermLengthMax
   let range
   if (min != null && max != null) {
      range = `from ${min} to ${max} months`
   } else if (min != null) {
      range = `of ${min} months or more`
   } else if (max != null) {
      range = `of ${max} months or less`
   } else {
      return ''
   }
   return `${name} is only available for Extended Term quotes ${range}.`
}

export {
   removeAllPromoDiscountForProduct,
   removePromoDiscountNotInCurrentBook,
   shouldDisableDiscount,
   shouldDisableDiscountFamilyOption,
   isProductOutsideTermRange,
   formatTermRange,
}
